import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Tabbar } from 'common-components'
import ActionsOverlay from 'common-components/ActionsOverlay'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { T } from 'consts/translationKeys'
import TagList from 'components/TagList'
import VideoItem from 'containers/SubSidebar/AIVideoContent/components/VideoItem'
import AIPromptText from 'components/AIPromptText'
import SmartPromptSection from 'containers/SubSidebar/AIVideoContent/GenerateTab/SmartPromptSection'
import Footer from 'containers/SubSidebar/AIVideoContent/GenerateTab/Footer'
import HistoryTab from 'containers/SubSidebar/AIVideoContent/HistoryTab'
import { getTabsData } from 'containers/SubSidebar/AIVideoContent/config'
import { TabKey } from 'containers/SubSidebar/AIVideoContent/consts'
import { useAiVideoItemTagsData } from 'containers/SubSidebar/AIVideoContent/hooks/useAiVideoItemTagsData'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { translate } from 'utils/translations'
import { SQUARE_ASPECT_RATIO } from 'utils/aspectRatio'
import { downloadMedia } from 'utils'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import EditIcon from 'assets/svgr-icons/Edit.svg'
import * as Styled from './styled'

const AIVideoOverlayContent = () => {
  const aiVideoOverlayData = useSelector(aiVideoSelectors.aiVideoOverlayData)
  const prompt = useSelector(aiVideoSelectors.prompt)
  const smartPrompt = useSelector(aiVideoSelectors.smartPrompt)
  const activeTab = useSelector(aiVideoSelectors.activeTab)
  const getItemTags = useAiVideoItemTagsData()
  const tabsData = getTabsData()

  const tags = getItemTags(
    aiVideoOverlayData.quality,
    aiVideoOverlayData.selectedModel
  )

  const handleTabClick = useCallback(
    (tab: string) => {
      if (tab !== activeTab) {
        dispatch(aiVideoThunks.setActiveTab(tab))
      }
    },
    [activeTab]
  )

  const handleManualEdit = useCallback(() => {
    // Clearing aiImageId disables smart edit mode (isSmartEditModeSelector
    // hard-requires it), so the Generate tab shows the normal form. The target
    // item must STAY set: it keeps the scene as the generation target, so a
    // re-generate after the manual edit (e.g. swapping the reference image)
    // replaces the scene in place. Clearing it routed the generation into a
    // brand-new clip and left the original scene's thumbnail stale.
    dispatch(aiVideoThunks.setAiImageId(null))
    dispatch(
      editorThunks.setActiveSidebarTabAndCloseOverlay(SUB_SIDEBAR_KEYS.AI_VIDEO)
    )
  }, [])

  const handleDownload = useCallback(async () => {
    if (!aiVideoOverlayData.videoUrl) return
    await downloadMedia({
      filePath: aiVideoOverlayData.videoUrl,
      fileName: `ai-video-${aiVideoOverlayData.targetMediaItemId}`
    })
  }, [aiVideoOverlayData.videoUrl, aiVideoOverlayData.targetMediaItemId])

  const overlayActions = useMemo(
    () => ({
      primaryActions: [
        {
          key: T.MANUAL_EDIT_LABEL,
          title: translate(T.MANUAL_EDIT_LABEL),
          icon: <EditIcon />,
          onClick: handleManualEdit
        },
        {
          key: T.DOWNLOAD,
          title: translate(T.DOWNLOAD),
          icon: <DownloadIcon />,
          onClick: handleDownload
        }
      ]
    }),
    [handleManualEdit, handleDownload]
  )

  return (
    <Styled.Container>
      <Styled.TabbarWrapper>
        <Tabbar
          data={tabsData.map(tab => ({ ...tab, id: tab.name }))}
          activeTabName={activeTab}
          setActiveTabName={handleTabClick}
        />
      </Styled.TabbarWrapper>
      {activeTab === TabKey.GENERATE ? (
        <>
          <Styled.Content>
            <TagList tags={tags} />
            <Styled.VideoItemWrapper>
              <VideoItem
                videoUrl={aiVideoOverlayData.videoUrl}
                status={aiVideoOverlayData.status}
                aspectRatio={
                  aiVideoOverlayData.aspectRatio || SQUARE_ASPECT_RATIO
                }
                additionalContent={<ActionsOverlay actions={overlayActions} />}
              />
            </Styled.VideoItemWrapper>
            {prompt && <AIPromptText promptText={prompt} />}
            <Styled.GradientDivider />
            <SmartPromptSection smartPrompt={smartPrompt} />
          </Styled.Content>
          <Footer isGenerateDisabled={!smartPrompt} />
        </>
      ) : (
        <Styled.Content>
          <HistoryTab variant="overlay" />
        </Styled.Content>
      )}
    </Styled.Container>
  )
}

export default AIVideoOverlayContent
